'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageFrame } from '../ui'
import { ChevronDown, Copy, Inbox, Loader2, MousePointerClick, RefreshCw } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import Turnstile, { type TurnstileHandle } from '../Turnstile'
import { OriginalSubmissionWidget } from '../ProcessedEntry/OriginalSubmissionWidget'
import { EntryDetailPanel } from '../EntryDetail/EntryDetailPanel'
import { apiGet, apiPost } from './entriesApi'
import { SearchableSelect } from './SearchableSelect'
import { SubmissionChatView } from './SubmissionChatView'
import { ScamClassificationForm } from './ScamClassificationForm'
import { EntryTimeline } from './EntryTimeline'
import { FeedPreviewCard } from './FeedPreviewCard'
import { PayloadWidget } from './PayloadWidget'
import type { Catalog, EntryRow, ScamData, UserEntryRevisionStatusType } from './entryTypes'
import { findCatalogMatchByName } from './entryTypes'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

type RevisionFilter = 'all' | 'unseen' | 'accepted' | 'reject' | 'defer1'
type ScamFilter = 'all' | 'scam' | 'not_scam'

const REVISION_FILTERS: { value: RevisionFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'unseen', label: 'Pendente' },
  { value: 'accepted', label: 'Aceite' },
  { value: 'reject', label: 'Rejeitado' },
  { value: 'defer1', label: 'Adiado' },
]

const SCAM_FILTERS: { value: ScamFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'scam', label: 'Scam' },
  { value: 'not_scam', label: 'Não scam' },
]

const isClassifySuccessStep = (step: string) =>
  step === 'classify_success' || step === 'classify_retry_success' || step?.startsWith('classify_manual_retry_success_')

export function getEntryIsScam(item: EntryRow): boolean | undefined {
  const steps = item.user_entry_pipeline_steps ?? []
  const latest = steps
    .filter((s) => isClassifySuccessStep(s.step) || s.step?.startsWith('classify_manual_update'))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
  const raw = item.raw_entries ?? item
  return latest?.json_info?.is_scam ?? raw.is_scam
}

function getProcessedEntry(item: EntryRow) {
  const processed = (item as any).processed_entries
  return Array.isArray(processed) ? processed[0] : processed
}

// `raw_entries.source` is the real ingestion-channel field (verified values:
// null, "twitter", "ligaram sites"). `processed_entries_flat.source_type` is a
// red herring — it's always the literal constant "processed_entry" and rarely
// even matches a given user_entries row, so it's useless as a filter.
export function getEntrySourceType(item: EntryRow): string | undefined {
  const raw = item.raw_entries ?? item
  return (raw as any)?.source
}

export function buildScamDataFromEntry(item: EntryRow): ScamData {
  const raw = item.raw_entries ?? item
  const steps = item.user_entry_pipeline_steps ?? []
  const sorted = steps
    .filter((s) => isClassifySuccessStep(s.step) || s.step?.startsWith('classify_manual_update'))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  const jsonInfo = sorted[0]?.json_info
  const translationInfo = sorted.find((s) => s.json_info?.translation)?.json_info
  return {
    isScam: jsonInfo?.is_scam ?? raw.is_scam,
    scamClaims: jsonInfo?.scam_claims ?? raw.scam_claims,
    scamValue: jsonInfo?.scam_value ?? raw.scam_value,
    translation: translationInfo?.translation ?? raw.translation,
    hidden: jsonInfo?.hidden ?? false,
    hideClassification: jsonInfo?.hide_classification ?? false,
  }
}

function replaceSixthAndNinth(phone?: string) {
  if (!phone || phone.length < 9) return phone
  const chars = phone.split('')
  chars[5] = '*'
  chars[8] = '*'
  return chars.join('')
}

function Collapsible({ title, theme, defaultOpen = false, children }: { title: string; theme: ProcessedTheme; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  const c = watchClasses(theme)
  return (
    <div className={`border-b ${c.line}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between px-3 py-2.5 text-[12px] font-semibold uppercase tracking-widest ${c.label}`}
      >
        {title}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  )
}

function PendingRow({ item, selected, theme, catalog, onClick }: { item: EntryRow; selected: boolean; theme: ProcessedTheme; catalog: Catalog; onClick: () => void }) {
  const c = watchClasses(theme)
  const raw = item.raw_entries ?? item
  const postType: string = raw.post_type ?? 'text'
  const preview = String(raw.message_content ?? '').slice(0, 60) || '—'
  const date = raw.created_at ? compactDate(raw.created_at) : ''

  const isCall = postType === 'call'
  const typeId = isCall ? raw.call_type_id : raw.message_type_id
  const typeText = isCall ? raw.call_type_text : raw.message_type_text
  const typeCollection = isCall ? catalog.callTypes : catalog.messageTypes
  const typeLabel =
    (typeId !== undefined && typeId !== null ? typeCollection.find((t) => String(t.id) === String(typeId))?.name : undefined)
    ?? typeText
    ?? findCatalogMatchByName(typeCollection, typeText)?.name
  const source = getEntrySourceType(item)

  const steps = item.user_entry_pipeline_steps ?? []
  const hasManualUpdate = steps.some((s) => s.step?.startsWith('classify_manual_update'))
  const hasClassify = steps.some((s) => isClassifySuccessStep(s.step) || s.step?.includes('failed')) || hasManualUpdate
  const classifyFailed = steps.some((s) => s.step?.includes('failed')) && !steps.some((s) => isClassifySuccessStep(s.step)) && !hasManualUpdate
  const isScam = getEntryIsScam(item)
  const revisionStatus = item.revision_status ?? 'unseen'

  const scamBadge = !hasClassify ? null : classifyFailed ? { text: 'erro', cls: 'bg-red-500/70' } : isScam === true ? { text: 'burla', cls: 'bg-destructive' } : isScam === false ? { text: 'não burla', cls: 'bg-success' } : { text: 'inconclusivo', cls: 'bg-warning' }
  const revisionBadge =
    revisionStatus === 'accept' ? { text: 'publicado', cls: 'bg-emerald-500/80' }
    : revisionStatus === 'accept-hidden' ? { text: 'oculto', cls: 'bg-subtle' }
    : revisionStatus === 'reject' ? { text: 'rejeitado', cls: 'bg-red-500/80' }
    : revisionStatus === 'defer1' ? { text: 'adiado', cls: 'bg-signal' }
    : null

  return (
    <button
      onClick={onClick}
      className={`flex w-full flex-col gap-1 border-b px-3 py-2.5 text-left transition-colors ${c.line} ${selected ? ('bg-signal-surface') : 'hover:bg-black/5'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className={`text-[13px] font-semibold ${c.text}`}>{postType.charAt(0).toUpperCase() + postType.slice(1)}</span>
          {typeLabel && (
            <span className={`shrink-0 truncate rounded-[4px] border px-1.5 py-0 text-[9px] font-semibold ${c.line} ${c.faint}`}>
              {typeLabel}
            </span>
          )}
          {source && (
            <span className={`shrink-0 truncate rounded-[4px] border px-1.5 py-0 text-[9px] font-semibold border-signal/30 text-signal`}>
              {source}
            </span>
          )}
        </div>
        <span className={`shrink-0 text-[10px] ${c.faint}`}>{date}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`flex-1 truncate text-[12px] ${c.muted}`}>{preview}</span>
        {scamBadge && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white ${scamBadge.cls}`}>{scamBadge.text}</span>}
        {revisionBadge && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white ${revisionBadge.cls}`}>{revisionBadge.text}</span>}
      </div>
    </button>
  )
}

function FilterChips<T extends string>({ value, options, theme, onChange }: { value: T; options: { value: T; label: string }[]; theme: ProcessedTheme; onChange: (v: T) => void }) {
  const c = watchClasses(theme)
  return (
    <div className="flex gap-1.5 overflow-x-auto px-3 py-2">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`shrink-0 rounded-[4px] border px-2.5 py-1 text-[11px] transition-colors ${
            value === o.value ? 'border-signal bg-signal-surface text-signal' : `${c.line} ${c.faint}`
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function useDragResize(initial: number, min: number, max: number) {
  const [width, setWidth] = useState(initial)
  const session = useRef<{ startX: number; startWidth: number; sign: 1 | -1 } | null>(null)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!session.current) return
      const { startX, startWidth, sign } = session.current
      const next = Math.min(max, Math.max(min, startWidth + sign * (e.clientX - startX)))
      setWidth(next)
    }
    const onUp = () => { session.current = null }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [min, max])

  const startDrag = (e: React.MouseEvent, sign: 1 | -1) => {
    session.current = { startX: e.clientX, startWidth: width, sign }
  }

  return { width, startDrag }
}

function DragHandle({ onMouseDown, theme }: { onMouseDown: (e: React.MouseEvent) => void; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  return (
    <div
      onMouseDown={onMouseDown}
      className={`w-1.5 shrink-0 cursor-col-resize border-x ${c.line} hover:bg-signal-surface`}
    />
  )
}

export function CasosView({ theme, catalog, catalogLoading }: { theme: ProcessedTheme; catalog: Catalog; catalogLoading: boolean }) {
  const c = watchClasses(theme)
  const sidebar = useDragResize(288, 220, 480)
  const sidePanel = useDragResize(320, 260, 520)
  const [entries, setEntries] = useState<EntryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [selected, setSelected] = useState<EntryRow | null>(null)
  const [revisionFilter, setRevisionFilter] = useState<RevisionFilter>('all')
  const [scamFilter, setScamFilter] = useState<ScamFilter>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [scamData, setScamData] = useState<ScamData>({})
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, any>>({})
  const [error, setError] = useState<string | undefined>()
  const [successMessage, setSuccessMessage] = useState<string | undefined>()
  const [moderationLoading, setModerationLoading] = useState<UserEntryRevisionStatusType | undefined>()
  const [retryClassifyLoading, setRetryClassifyLoading] = useState(false)
  const [retryClassifyStatus, setRetryClassifyStatus] = useState<'idle' | 'success' | 'error'>('idle')

  // Turnstile-gated R2 image load for the original submission screenshot —
  // same pattern as OriginalSubmissionWidget's other callers in BackofficeApp.tsx.
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)
  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])
  const getScreenshotToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      pendingTokenRef.current = resolve
      const runWhenReady = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => runWhenReady(attempt + 1), 100)
      }
      runWhenReady()
    })
  }, [])

  const fetchEntries = async (p: number, filter: RevisionFilter) => {
    p === 0 ? setLoading(true) : setLoadingMore(true)
    const statusParam = filter !== 'all' ? `&revision_status=${filter}` : ''
    const result = await apiGet<{ entries: EntryRow[]; hasMore: boolean }>(`?page=${p}${statusParam}`)
    if (result.success && result.data) {
      setEntries((prev) => (p === 0 ? result.data!.entries : [...prev, ...result.data!.entries]))
      setHasMore(result.data.hasMore)
      setPage(p)
    } else {
      setError(result.error ?? 'Erro ao obter os dados')
    }
    p === 0 ? setLoading(false) : setLoadingMore(false)
  }

  useEffect(() => { fetchEntries(0, 'all') }, [])

  useEffect(() => {
    if (!selected) return
    setScamData(buildScamDataFromEntry(selected))
    setFieldOverrides({})
    setModerationLoading(undefined)
    setRetryClassifyStatus('idle')
  }, [selected?.id])

  useEffect(() => {
    if (!selected) return
    const updated = entries.find((e) => e.id === selected.id)
    if (updated) setSelected(updated)
  }, [entries])

  const handleRevisionFilterChange = (filter: RevisionFilter) => {
    setRevisionFilter(filter)
    setSelected(null)
    fetchEntries(0, filter)
  }

  const sourceFilterOptions = useMemo(() => {
    return [...new Set(entries.map((e) => getEntrySourceType(e)).filter(Boolean))] as string[]
  }, [entries])

  const filteredEntries = useMemo(() => {
    let result = entries
    if (sourceFilter !== 'all') result = result.filter((item) => getEntrySourceType(item) === sourceFilter)
    if (scamFilter === 'all') return result
    return result.filter((item) => {
      const isScam = getEntryIsScam(item)
      if (scamFilter === 'scam') return isScam === true
      if (scamFilter === 'not_scam') return isScam === false
      return true
    })
  }, [entries, scamFilter, sourceFilter])

  // Most cases in Casos (especially "Pendente") haven't been through the
  // accept pipeline yet and have no processed_entries row at all — fall back
  // to raw_entries as the base so the Feed preview still has something to
  // show, layering processed_entries fields (once they exist) on top.
  const selectedProcessed = useMemo(() => {
    if (!selected) return undefined
    const raw = selected.raw_entries ?? selected
    const processed = getProcessedEntry(selected)
    return { ...raw, ...(processed ?? {}), ...fieldOverrides }
  }, [selected, fieldOverrides])

  const selectedEditableFields = useMemo(() => {
    if (!selected) return undefined
    const processed = getProcessedEntry(selected) ?? {}
    const raw = selected.raw_entries ?? selected
    return { ...raw, ...processed, ...fieldOverrides }
  }, [selected, fieldOverrides])

  const selectedR2Key: string | undefined = (selected?.raw_entries ?? selected)?.r2_key
  const selectedOriginalPayload: Record<string, unknown> | undefined = (selected?.raw_entries ?? selected)?.original_payload
  const selectedParsedPayload: Record<string, unknown> | undefined = (selected?.raw_entries ?? selected)?.parsed_payload

  // Same "call uses call_type, everything else uses message_type" convention
  // already applied to the Tipo <select> below, reused here to resolve the id
  // to its catalog label for OriginalSubmissionWidget.
  const selectedMessageTypeId = selectedEditableFields
    ? (selectedEditableFields.post_type === 'call' ? selectedEditableFields.call_type_id : selectedEditableFields.message_type_id)
    : undefined
  const selectedMessageTypeText = useMemo(() => {
    if (!selectedEditableFields || selectedMessageTypeId == null) return undefined
    const list = selectedEditableFields.post_type === 'call' ? catalog.callTypes : catalog.messageTypes
    return list.find((t) => String(t.id) === String(selectedMessageTypeId))?.name
  }, [selectedEditableFields, selectedMessageTypeId, catalog])

  const currentRevisionStatus = selected?.revision_status as string | undefined
  const isModerationActionDisabled = (status: UserEntryRevisionStatusType) =>
    Boolean(moderationLoading) || currentRevisionStatus === status || (status === 'accept' && currentRevisionStatus === 'auto-accept')

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(undefined), 3000)
  }

  const handleCopyEntryId = async () => {
    if (!selected?.id) return
    await navigator.clipboard.writeText(String(selected.id))
    showSuccess('ID copiado')
  }

  const handleEditRawField = async (field: 'platform' | 'messageType' | 'callType', id?: string, text?: string) => {
    if (!selected) return
    const raw = selected.raw_entries ?? selected
    const isCall = raw?.post_type === 'call'
    const delta: Record<string, any> = {}
    if (field === 'platform') {
      delta.platform_id = id !== undefined && id !== '' ? Number(id) : null
    } else if (field === 'messageType') {
      if (isCall) {
        delta.call_type_id = id !== undefined && id !== '' ? Number(id) : null
        if (text !== undefined) delta.call_type_text = text ?? null
      } else {
        delta.message_type_id = id !== undefined && id !== '' ? Number(id) : null
      }
    }
    setFieldOverrides((prev) => ({ ...prev, ...delta }))

    if (selected.posted) {
      const payload: Record<string, any> = { userEntryId: selected.id }
      if ('platform_id' in delta) payload.platformId = delta.platform_id
      if ('message_type_id' in delta) payload.messageTypeId = delta.message_type_id
      if ('call_type_id' in delta) payload.callTypeId = delta.call_type_id
      if ('call_type_text' in delta) payload.callTypeText = delta.call_type_text
      const result = await apiPost('/fields', payload)
      if (result.success) showSuccess('Campos atualizados com sucesso')
      else setError('Erro ao guardar campo')
    }
  }

  const handlePublish = async (accept: UserEntryRevisionStatusType) => {
    if (!selected) return
    if (isModerationActionDisabled(accept)) return
    setModerationLoading(accept)
    const raw = { ...(selected.raw_entries ?? selected), ...fieldOverrides }
    // ProcessedEntrySchema (backend) is an exact-match struct: no extra keys (no
    // `translation` — that's classification-only, sent via /classification), and
    // required-but-nullable keys (text/messageTypeId/isScam/scamClaims/scamValue)
    // must be explicitly `null` rather than `undefined`, since JSON.stringify
    // drops `undefined` values and the backend would see the key as missing.
    const payload = {
      postType: raw.post_type,
      from: raw.post_type === 'call' ? replaceSixthAndNinth(raw.sender) : raw.sender,
      text: raw.message_content ?? null,
      subject: raw.message_subject ?? '',
      platformId: raw.platform_id,
      messageTypeId: raw.message_type_id ?? null,
      callTypeId: raw.call_type_id,
      callType: raw.call_type_text,
      isScam: scamData.isScam ?? null,
      scamClaims: scamData.scamClaims ?? null,
      scamValue: scamData.scamValue ?? null,
      rawEntryId: selected.raw_entry_id ?? raw.id,
      revisionStatus: accept,
      created_at: raw.created_at,
      updated_at: raw.created_at,
    }
    const result = await apiPost('/accept', payload)
    if (result.success) {
      setSelected(null)
      fetchEntries(0, revisionFilter)
    } else {
      setError(typeof result.error === 'string' ? result.error : 'Erro ao publicar entrada')
    }
    setModerationLoading(undefined)
  }

  const handleReject = async (revisionStatus: UserEntryRevisionStatusType) => {
    if (!selected) return
    if (isModerationActionDisabled(revisionStatus)) return
    setModerationLoading(revisionStatus)
    const raw = selected.raw_entries ?? selected
    const result = await apiPost('/reject', { rawEntryId: raw.id, revisionStatus })
    if (result.success) {
      setSelected(null)
      fetchEntries(0, revisionFilter)
    } else {
      setError('Erro ao rejeitar entrada')
    }
    setModerationLoading(undefined)
  }

  const handleSaveClassification = async (overrideTranslation?: any) => {
    if (!selected) return
    const translationToSave = overrideTranslation !== undefined ? overrideTranslation : scamData.translation
    const existingManualCount = (selected.user_entry_pipeline_steps ?? []).filter((s) => s.step?.startsWith('classify_manual_update')).length
    const stepName = `classify_manual_update_${existingManualCount + 1}`
    const result = await apiPost('/classification', {
      userEntryId: selected.id,
      step: stepName,
      isScam: scamData.isScam,
      scamClaims: scamData.scamClaims,
      scamValue: scamData.scamValue,
      translation: translationToSave,
      hidden: scamData.hidden,
      hideClassification: scamData.hideClassification,
    })
    if (result.success) {
      if (overrideTranslation !== undefined) setScamData((prev) => ({ ...prev, translation: translationToSave }))
      const newStep = {
        user_entry_id: selected.id,
        step: stepName,
        created_at: new Date().toISOString(),
        json_info: {
          is_scam: scamData.isScam,
          scam_claims: scamData.scamClaims,
          scam_value: scamData.scamValue,
          translation: translationToSave,
          hidden: scamData.hidden,
          hide_classification: scamData.hideClassification,
        },
      }
      setSelected((prev) => (prev ? { ...prev, user_entry_pipeline_steps: [...(prev.user_entry_pipeline_steps ?? []), newStep] } : prev))
      showSuccess('Classificação guardada')
    } else {
      setError('Erro ao guardar classificação')
    }
  }

  // force: re-runs the full classify_e34 intake in the background and makes
  // Python refresh what it already linked (entity_reference beneficiary
  // included) instead of deduping the re-run away. The response is only the
  // ack that it started — the result lands as classify_success from Python.
  const handleRetryClassify = async () => {
    if (!selected) return
    const raw = selected.raw_entries ?? selected
    setRetryClassifyLoading(true)
    setRetryClassifyStatus('idle')
    const result = await apiPost('/retry-classify', { userEntryId: selected.id, rawEntryId: raw?.id, force: true })
    setRetryClassifyLoading(false)
    if (result.success) {
      setRetryClassifyStatus('success')
      fetchEntries(0, revisionFilter)
    } else {
      setRetryClassifyStatus('error')
    }
  }

  const buildSubmission = (item: EntryRow) => {
    const raw = item.raw_entries ?? item
    const processed = getProcessedEntry(item)
    return {
      ...item,
      pipeline_steps: item.user_entry_pipeline_steps ?? [],
      posted: true,
      raw_entries: { ...(raw ?? {}), ...(processed ?? {}), ...fieldOverrides, id: raw?.id, raw_entry_id: item.raw_entry_id ?? raw?.id },
    }
  }

  return (
    <PageFrame padded={false} fill eyebrow="Entries" title="Casos">
    <div className="flex h-full min-h-0">
      <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
      {/* Sidebar: case list */}
      <div className="flex shrink-0 flex-col" style={{ width: sidebar.width }}>
        <div className={`flex items-center justify-between border-b px-3 py-2.5 ${c.line}`}>
          <span className={`text-[13px] font-semibold ${c.text}`}>Casos</span>
          <button onClick={() => fetchEntries(0, revisionFilter)} className={c.faint}>
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        <FilterChips value={revisionFilter} options={REVISION_FILTERS} theme={theme} onChange={handleRevisionFilterChange} />
        <FilterChips value={scamFilter} options={SCAM_FILTERS} theme={theme} onChange={setScamFilter} />
        {sourceFilterOptions.length > 0 && (
          <FilterChips
            value={sourceFilter}
            options={[{ value: 'all', label: 'Todas fontes' }, ...sourceFilterOptions.map((s) => ({ value: s, label: s }))]}
            theme={theme}
            onChange={setSourceFilter}
          />
        )}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-signal" />
            </div>
          )}
          {!loading && filteredEntries.length === 0 && (
            <div className={`flex flex-col items-center gap-2 py-10 ${c.faint}`}>
              <Inbox className="h-8 w-8 opacity-40" />
              <span className="text-[12px]">Sem entradas</span>
            </div>
          )}
          {!loading && filteredEntries.map((item) => (
            <PendingRow key={item.id} item={item} selected={selected?.id === item.id} theme={theme} catalog={catalog} onClick={() => setSelected(item)} />
          ))}
          {hasMore && !loading && (
            <button
              onClick={() => fetchEntries(page + 1, revisionFilter)}
              disabled={loadingMore}
              className={`w-full py-3 text-center text-[12px] text-signal`}
            >
              {loadingMore ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Carregar mais'}
            </button>
          )}
        </div>
      </div>

      <DragHandle theme={theme} onMouseDown={(e) => sidebar.startDrag(e, 1)} />

      {/* Detail: chat + actions */}
      {!selected ? (
        <div className={`flex flex-1 flex-col items-center justify-center gap-2 ${c.faint}`}>
          <MousePointerClick className="h-8 w-8 opacity-40" />
          <span className="text-[13px]">Seleciona um caso</span>
        </div>
      ) : (
        <>
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className={`flex items-center gap-3 border-b px-3 py-2 ${c.line}`}>
              <span className={`text-[11px] ${c.faint}`}>User Entry ID: {selected.id}</span>
              <button onClick={handleCopyEntryId} className={`flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${c.line} ${c.muted}`}>
                <Copy className="h-3 w-3" />
                Copiar
              </button>
              {error && <span className="text-[11px] text-destructive">{error}</span>}
              {successMessage && <span className="text-[11px] text-success">{successMessage}</span>}
            </div>
            {catalogLoading ? (
              <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-signal" /></div>
            ) : (
              <div className="flex-1 overflow-y-auto p-3">
                {/* Compact by design — a fixed max-height instead of filling the
                    whole pane like the old full-height chat did. SubmissionChatView
                    manages its own internal scroll, and EntryDetailPanel's built-in
                    expand button (top-right) pops the full conversation into a modal
                    when a moderator wants to actually read the whole thing. */}
                <EntryDetailPanel label="Rendered Conversation" theme={theme} cols={1} noPadding className="max-h-[420px]">
                  <SubmissionChatView
                    submission={buildSubmission(selected)}
                    catalog={catalog}
                    theme={theme}
                    onSaveClassification={handleSaveClassification}
                    onEditRawField={handleEditRawField}
                  />
                </EntryDetailPanel>
              </div>
            )}
          </div>

          <DragHandle theme={theme} onMouseDown={(e) => sidePanel.startDrag(e, -1)} />

          <div className="flex shrink-0 flex-col overflow-hidden" style={{ width: sidePanel.width }}>
          <EntryDetailPanel label="Case Actions" theme={theme} cols={1} noPadding scrollable className="h-full">
            <div className={`flex flex-wrap items-center gap-2 border-b p-2.5 ${c.line}`}>
              <button
                onClick={() => handleReject('reject')}
                disabled={isModerationActionDisabled('reject')}
                className="rounded border border-destructive px-3 py-1.5 text-[12px] font-semibold text-destructive transition-colors hover:bg-danger-surface disabled:opacity-40"
              >
                {moderationLoading === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Reject'}
              </button>
              <button
                onClick={() => handlePublish('accept')}
                disabled={isModerationActionDisabled('accept')}
                className="rounded bg-success text-success-foreground px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-success disabled:opacity-40"
              >
                {moderationLoading === 'accept' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Accept'}
              </button>
              <button
                onClick={() => handlePublish('accept-hidden')}
                disabled={isModerationActionDisabled('accept-hidden')}
                className={`rounded px-3 py-1.5 text-[12px] font-semibold text-white transition-colors disabled:opacity-40 ${theme === 'dark' ? 'bg-muted hover:bg-muted' : 'bg-subtle hover:bg-muted'}`}
              >
                Accept Hidden
              </button>
              <button
                onClick={() => handleReject('defer1')}
                disabled={isModerationActionDisabled('defer1')}
                className={`rounded border px-3 py-1.5 text-[12px] font-semibold ${c.line} ${c.muted} disabled:opacity-40`}
              >
                Defer
              </button>
            </div>

            {selectedR2Key && (
              <div className={`border-b p-3 ${c.line}`}>
                <div className="w-[140px]">
                  <OriginalSubmissionWidget
                    r2Key={selectedR2Key}
                    theme={theme}
                    getToken={getScreenshotToken}
                    bare
                    postType={selectedEditableFields?.post_type}
                    messageTypeId={selectedMessageTypeId}
                    messageTypeText={selectedMessageTypeText}
                  />
                </div>
              </div>
            )}

            <Collapsible title="Conteúdo" theme={theme} defaultOpen>
              <div className="space-y-2.5 px-3">
                <div>
                  <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Remetente</div>
                  <Input
                    className={c.input}
                    value={selectedEditableFields?.sender ?? ''}
                    onChange={(e) => setFieldOverrides((prev) => ({ ...prev, sender: e.target.value }))}
                    placeholder="Remetente"
                  />
                </div>
                <div>
                  <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Criado em</div>
                  <Input
                    className={c.input}
                    value={selectedEditableFields?.created_at ?? ''}
                    onChange={(e) => setFieldOverrides((prev) => ({ ...prev, created_at: e.target.value }))}
                    placeholder="created_at (ISO)"
                  />
                </div>
                {selectedEditableFields?.post_type !== 'call' && (
                  <>
                    <div>
                      <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Assunto</div>
                      <Input
                        className={c.input}
                        value={selectedEditableFields?.message_subject ?? ''}
                        onChange={(e) => setFieldOverrides((prev) => ({ ...prev, message_subject: e.target.value }))}
                        placeholder="Assunto"
                      />
                    </div>
                    <div>
                      <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Conteúdo</div>
                      <Textarea
                        className={c.input}
                        value={selectedEditableFields?.message_content ?? ''}
                        onChange={(e) => setFieldOverrides((prev) => ({ ...prev, message_content: e.target.value }))}
                        placeholder="Conteúdo da mensagem"
                        rows={5}
                      />
                    </div>
                  </>
                )}
                <p className={`text-[10px] ${c.faint}`}>
                  Alterações aplicadas apenas ao clicar Accept{selected.posted ? ' (atualiza a entrada publicada)' : ''}.
                </p>
              </div>
            </Collapsible>

            <Collapsible title="Feed" theme={theme} defaultOpen>
              {selectedProcessed ? (
                <div className="px-3">
                  <FeedPreviewCard entry={selectedProcessed} catalog={catalog} theme={theme} />
                  {selectedEditableFields && (
                    <div className="mt-3 space-y-2.5">
                      <div>
                        <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Tipo</div>
                        <select
                          className={`h-8 w-full rounded-md border px-2 text-[12px] ${c.input}`}
                          value={(selectedEditableFields.post_type === 'call' ? selectedEditableFields.call_type_id : selectedEditableFields.message_type_id) ?? ''}
                          onChange={(e) => {
                            const isCallType = selectedEditableFields.post_type === 'call'
                            const list = isCallType ? catalog.callTypes : catalog.messageTypes
                            const match = list.find((t) => String(t.id) === e.target.value)
                            handleEditRawField(isCallType ? 'callType' : 'messageType', e.target.value || undefined, match?.name)
                          }}
                        >
                          <option value="">—</option>
                          {(selectedEditableFields.post_type === 'call' ? catalog.callTypes : catalog.messageTypes).map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Plataforma</div>
                        <SearchableSelect
                          theme={theme}
                          value={selectedEditableFields.platform_id ?? undefined}
                          options={catalog.platforms}
                          placeholder="—"
                          searchPlaceholder="Procurar plataforma…"
                          noneLabel="—"
                          onChange={(id, name) => handleEditRawField('platform', id || undefined, name)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={`px-3 py-4 text-center text-[12px] ${c.faint}`}>Sem entrada processada</div>
              )}
            </Collapsible>

            <Collapsible title="Payload" theme={theme}>
              <div className="px-3">
                <PayloadWidget
                  originalPayload={selectedOriginalPayload}
                  parsedPayload={selectedParsedPayload}
                  theme={theme}
                />
              </div>
            </Collapsible>

            <Collapsible title="Linha do tempo" theme={theme}>
              <EntryTimeline item={selected} theme={theme} />
            </Collapsible>

            <Collapsible title="Classificação" theme={theme} defaultOpen>
              <div className="px-3">
                <button
                  onClick={() => handleSaveClassification()}
                  className="mb-2 w-full rounded bg-primary text-primary-foreground py-1.5 text-[12px] font-semibold text-white hover:bg-signal"
                >
                  Guardar classificação
                </button>
                <button
                  onClick={handleRetryClassify}
                  disabled={retryClassifyLoading}
                  className={`mb-2 w-full rounded border py-1.5 text-[12px] font-semibold ${c.line} ${
                    retryClassifyStatus === 'success' ? 'text-success' : retryClassifyStatus === 'error' ? 'text-destructive' : c.muted
                  }`}
                >
                  {retryClassifyLoading ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" />
                    : retryClassifyStatus === 'success' ? 'Classificação iniciada ✓'
                    : retryClassifyStatus === 'error' ? 'Falhou — tentar de novo'
                    : 'Forçar classificação'}
                </button>
                <ScamClassificationForm scamData={scamData} setScamData={setScamData} theme={theme} />
              </div>
            </Collapsible>
          </EntryDetailPanel>
          </div>
        </>
      )}
    </div>
    </PageFrame>
  )
}
